// v3.5.0 - 2026-02-02 - CSS bar chart + period filter (no Recharts)

import { useCallback, useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/lib/stores/authStore'
import { useQuery } from '@/lib/hooks'
import { cer } from '@/lib/api/client'
import { PageContainer, PageHeader } from '@/components/layout'
import { Card, CardContent, CardHeader, CardTitle, Badge, Button, Spinner } from '@/components/ui'

function formatNumber(num: number): string {
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num)
}

function formatCurrency(num: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(num)
}

function formatPeriod(period: unknown): string {
  // Ensure period is a string - handle objects, numbers, null, undefined
  if (period === null || period === undefined) return 'N/D'
  if (typeof period === 'object') {
    console.warn('[formatPeriod] Received object instead of string:', period)
    return 'N/D'
  }
  const periodStr = String(period)
  if (!periodStr) return 'N/D'
  try {
    if (periodStr.includes('/')) {
      const [year, range] = periodStr.split('-')
      const [startMonth, endMonth] = range.split('/')
      const monthNames = [
        'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
        'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'
      ]
      return `${monthNames[parseInt(startMonth) - 1]}-${monthNames[parseInt(endMonth) - 1]} ${year}`
    }
    const [year, month] = periodStr.split('-')
    const monthNames = [
      'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
    ]
    const monthIdx = parseInt(month) - 1
    if (isNaN(monthIdx) || monthIdx < 0 || monthIdx > 11) return periodStr
    return `${monthNames[monthIdx]} ${year}`
  } catch {
    return periodStr || 'N/D'
  }
}

function formatDate(dateString: unknown): string {
  // Ensure dateString is a string - handle objects, numbers, null, undefined
  if (dateString === null || dateString === undefined) return 'N/D'
  if (typeof dateString === 'object') {
    console.warn('[formatDate] Received object instead of string:', dateString)
    return 'N/D'
  }
  const dateStr = String(dateString)
  if (!dateStr) return 'N/D'
  try {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateStr || 'N/D'
  }
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    active: 'Attivo',
    inactive: 'Inattivo',
    pending: 'In Attesa',
    draft: 'Bozza',
    approved: 'Approvato',
    distributed: 'Distribuito',
  }
  return labels[status] || status
}

function getStatusBadgeVariant(status: string): 'default' | 'success' | 'warning' | 'error' | 'info' {
  const variants: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
    active: 'success',
    inactive: 'error',
    pending: 'warning',
    draft: 'default',
    approved: 'success',
    distributed: 'info',
  }
  return variants[status] || 'default'
}

function getTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    producer: 'Produttore',
    consumer: 'Consumatore',
    prosumer: 'Prosumer',
  }
  return labels[type] || type
}

function getTypeBadgeVariant(type: string): 'default' | 'success' | 'warning' {
  const variants: Record<string, 'default' | 'success' | 'warning'> = {
    producer: 'success',
    consumer: 'default',
    prosumer: 'warning',
  }
  return variants[type] || 'default'
}

// Simple bar chart component using CSS
interface BarChartData {
  period: string
  EACI: number
  TI: number
}

function SimpleBarChart({ data }: { data: BarChartData[] }) {
  if (!data || data.length === 0) return null

  const maxEACI = Math.max(...data.map(d => d.EACI))
  const maxTI = Math.max(...data.map(d => d.TI))

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-amber-500 rounded" />
          <span>EACI (kWh)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded" />
          <span>TI (EUR)</span>
        </div>
      </div>
      {data.map((item, idx) => (
        <div key={idx} className="group">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>{item.period}</span>
            <span className="opacity-0 group-hover:opacity-100 transition-opacity">
              EACI: {item.EACI.toFixed(0)} kWh | TI: {item.TI.toFixed(2)} EUR
            </span>
          </div>
          <div className="flex gap-1 h-6">
            <div
              className="bg-amber-500 rounded-sm transition-all hover:bg-amber-600"
              style={{ width: `${maxEACI > 0 ? (item.EACI / maxEACI) * 50 : 0}%` }}
              title={`EACI: ${item.EACI.toFixed(2)} kWh`}
            />
            <div
              className="bg-green-500 rounded-sm transition-all hover:bg-green-600"
              style={{ width: `${maxTI > 0 ? (item.TI / maxTI) * 50 : 0}%` }}
              title={`TI: ${item.TI.toFixed(2)} EUR`}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

// Period filter component
function PeriodFilter({
  periods,
  selected,
  onChange
}: {
  periods: string[]
  selected: string[]
  onChange: (periods: string[]) => void
}) {
  const togglePeriod = (period: string) => {
    if (selected.includes(period)) {
      onChange(selected.filter(p => p !== period))
    } else {
      onChange([...selected, period])
    }
  }

  const selectAll = () => onChange([...periods])
  const clearAll = () => onChange([])

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground mr-2">Periodi:</span>
      <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs">
        Tutti
      </Button>
      <Button variant="ghost" size="sm" onClick={clearAll} className="text-xs">
        Nessuno
      </Button>
      <div className="w-px h-4 bg-border mx-1" />
      {periods.map(period => (
        <button
          key={period}
          onClick={() => togglePeriod(period)}
          className={`px-2 py-1 text-xs rounded-md transition-colors ${
            selected.includes(period)
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          {period}
        </button>
      ))}
    </div>
  )
}

export function ConfigurationDetail() {
  const { configId } = useParams<{ configId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  // Period filter state
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([])

  const fetchConfigDetail = useCallback(async () => {
    if (!user?.cer_id || !configId) {
      return null
    }
    try {
      const result = await cer.getConfigurationDetail(user.cer_id, configId)
      return result
    } catch (err) {
      console.error('[ConfigurationDetail] API error:', err)
      throw err
    }
  }, [user?.cer_id, configId])

  const { data, isLoading, error } = useQuery(fetchConfigDetail, {
    enabled: !!user?.cer_id && !!configId,
  })

  // Handle case when user is not yet loaded
  if (!user) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    )
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-600">
          Errore nel caricamento della configurazione: {error}
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/configurations')} className="mt-4">
          Torna alle Configurazioni
        </Button>
      </PageContainer>
    )
  }

  if (!data) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Configurazione non trovata</p>
          <Button variant="outline" onClick={() => navigate('/admin/configurations')}>
            Torna alle Configurazioni
          </Button>
        </div>
      </PageContainer>
    )
  }

  // Validate data structure to prevent React #310 errors
  if (!data.configuration || typeof data.configuration !== 'object') {
    console.error('[ConfigurationDetail] Invalid data structure - missing configuration:', data)
    return (
      <PageContainer>
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-600">
          Errore: Struttura dati non valida
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/configurations')} className="mt-4">
          Torna alle Configurazioni
        </Button>
      </PageContainer>
    )
  }

  const { configuration, members, energy, tiHistory } = data

  // Extract available periods from tiHistory
  const availablePeriods = useMemo(() => {
    if (!tiHistory || !Array.isArray(tiHistory)) return []
    return tiHistory
      .map(calc => String(calc?.period || ''))
      .filter(p => p)
      .reverse() // Oldest first
  }, [tiHistory])

  // Initialize selected periods to all available (on first load)
  useMemo(() => {
    if (selectedPeriods.length === 0 && availablePeriods.length > 0) {
      setSelectedPeriods(availablePeriods)
    }
  }, [availablePeriods, selectedPeriods.length])

  // Filter and prepare chart data
  const chartData = useMemo(() => {
    if (!tiHistory || !Array.isArray(tiHistory) || tiHistory.length === 0) return []

    const filtered = selectedPeriods.length > 0
      ? tiHistory.filter(calc => selectedPeriods.includes(String(calc?.period || '')))
      : tiHistory

    return filtered
      .slice()
      .reverse()
      .map(calc => ({
        period: formatPeriod(String(calc?.period || '')),
        EACI: Number(calc?.eaciTotal) || 0,
        TI: Number(calc?.tiTotal) || 0,
      }))
  }, [tiHistory, selectedPeriods])

  // Filtered tiHistory for the list
  const filteredTiHistory = useMemo(() => {
    if (!tiHistory || !Array.isArray(tiHistory)) return []
    if (selectedPeriods.length === 0) return tiHistory
    return tiHistory.filter(calc => selectedPeriods.includes(String(calc?.period || '')))
  }, [tiHistory, selectedPeriods])

  // Ensure configuration fields are strings, not objects
  const safeCrin = typeof configuration.crin === 'string' ? configuration.crin : String(configuration.crin || 'N/D')
  const safeZona = typeof configuration.zona === 'string' ? configuration.zona : String(configuration.zona || 'N/D')

  return (
    <PageContainer>
      <PageHeader
        title={`Configurazione ${safeCrin}`}
        description={`Zona ${safeZona} - Dettagli e statistiche`}
        actions={
          <Button variant="outline" onClick={() => navigate('/admin/configurations')}>
            <svg
              className="h-4 w-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Indietro
          </Button>
        }
      />

      <div className="space-y-6">
        {/* Configuration Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Informazioni Configurazione</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-sm text-muted-foreground">CRIN</p>
                <p className="text-lg font-semibold font-mono">{configuration.crin}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Zona</p>
                <p className="text-lg font-semibold">{configuration.zona || 'Non specificata'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Cabina Primaria</p>
                <p className="text-lg font-semibold">{configuration.cabinaPrimaria || 'Non specificata'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge variant={getStatusBadgeVariant(configuration.status)} className="mt-1">
                  {getStatusLabel(configuration.status)}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Energy Summary Card */}
        <Card>
          <CardHeader>
            <CardTitle>Riepilogo Energia</CardTitle>
          </CardHeader>
          <CardContent>
            {energy.lastPeriod ? (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  Totali cumulati - Ultimo periodo: {formatPeriod(energy.lastPeriod)}
                </p>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="p-4 rounded-lg bg-green-50 border border-green-100">
                    <p className="text-sm text-green-700">Energia Immessa</p>
                    <p className="text-xl font-semibold text-green-800 mt-1">
                      {formatNumber(energy.totalImmessa)} kWh
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                    <p className="text-sm text-blue-700">Energia Prelevata</p>
                    <p className="text-xl font-semibold text-blue-800 mt-1">
                      {formatNumber(energy.totalPrelevata)} kWh
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
                    <p className="text-sm text-amber-700">EACI Condivisa</p>
                    <p className="text-xl font-semibold text-amber-800 mt-1">
                      {formatNumber(energy.totalEaci)} kWh
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">Nessun dato energetico disponibile</p>
            )}
          </CardContent>
        </Card>

        {/* Period Filter + Chart */}
        {availablePeriods.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle>Trend EACI e TI</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/admin/energy-data?configId=${configId}`)}
                >
                  Vedi dati energia
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <PeriodFilter
                periods={availablePeriods}
                selected={selectedPeriods}
                onChange={setSelectedPeriods}
              />
              {chartData.length > 0 ? (
                <SimpleBarChart data={chartData} />
              ) : (
                <p className="text-muted-foreground text-sm">Seleziona almeno un periodo per visualizzare il grafico</p>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          {/* Members Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Membri Associati</CardTitle>
                <Badge variant="default">{members.length} membri</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {members.length > 0 ? (
                <div className="space-y-3">
                  {members.map((member) => {
                    // Calculate max for mini bars (across all members)
                    const maxEnergy = Math.max(...members.map(m => Math.max(m.energy?.totalImmessa || 0, m.energy?.totalPrelevata || 0)), 1)

                    return (
                      <Link
                        key={member.id}
                        to={`/admin/members/${member.id}`}
                        className="block p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-medium">{member.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {member.pod ? <span className="font-mono text-xs">{member.pod}</span> : 'POD N/D'} | {getTypeLabel(member.type)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={getTypeBadgeVariant(member.type)}>
                              {getTypeLabel(member.type)}
                            </Badge>
                            {member.pnrrPercent > 0 && (
                              <Badge variant="default" className="text-xs">
                                PNRR {member.pnrrPercent}%
                              </Badge>
                            )}
                            <svg className="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                        {/* Mini energy bars */}
                        {member.energy && (member.energy.totalImmessa > 0 || member.energy.totalPrelevata > 0) && (
                          <div className="flex gap-2 items-center mt-2">
                            <div className="flex-1 flex gap-1 h-2">
                              <div
                                className="bg-green-500 rounded-sm"
                                style={{ width: `${(member.energy.totalImmessa / maxEnergy) * 100}%` }}
                                title={`Immessa: ${formatNumber(member.energy.totalImmessa)} kWh`}
                              />
                              <div
                                className="bg-blue-500 rounded-sm"
                                style={{ width: `${(member.energy.totalPrelevata / maxEnergy) * 100}%` }}
                                title={`Prelevata: ${formatNumber(member.energy.totalPrelevata)} kWh`}
                              />
                            </div>
                            <div className="text-xs text-muted-foreground w-20 text-right">
                              {formatCurrency(member.energy.tiTotal)}
                            </div>
                          </div>
                        )}
                      </Link>
                    )
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground">Nessun membro associato a questa configurazione</p>
              )}
            </CardContent>
          </Card>

          {/* TI Calculations Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Calcoli TI Recenti</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin/calculations')}
                >
                  Vedi tutti
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {filteredTiHistory.length > 0 ? (
                <div className="space-y-3">
                  {filteredTiHistory.map((calc) => (
                    <Link
                      key={calc.id}
                      to={`/admin/calculations/${calc.id}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div>
                        <p className="font-medium">{formatPeriod(calc.period)}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(calc.calculatedAt)} | EACI: {formatNumber(calc.eaciTotal)} kWh
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="font-semibold">{formatCurrency(calc.tiTotal)}</p>
                          <Badge variant={getStatusBadgeVariant(calc.status)} className="mt-1">
                            {getStatusLabel(calc.status)}
                          </Badge>
                        </div>
                        <svg className="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">Nessun calcolo TI disponibile</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Azioni Rapide</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/energy-data?configId=${configId}`)}
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                  />
                </svg>
                Browser Dati Energia
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/upload?configId=${configId}`)}
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                Carica Dati Energia
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/calculations?configId=${configId}`)}
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                Nuovo Calcolo TI
              </Button>
              <Button variant="outline">
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Esporta Report
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  )
}
